import { Fragment } from 'react'
import { Listbox, Transition } from '@headlessui/react'
import { ChevronUpDownIcon } from '@heroicons/react/20/solid'
import classNames from 'classnames'
import StatusPill from '../../components/Badges/StatusPill'
import Pill from '../Badges/Pill'

interface Option<T> {
  id: string | number
  value: T
  label: string
  color: 'green' | 'yellow' | 'orange'
}

export interface StandalonePillBoxMultiSelectProps<T> {
  placeholder: string
  value: T[]
  options: Option<T>[]
  onChange: (value: T[]) => void
}

export default function StandalonePillBoxMultiSelect<T>({
  placeholder,
  value,
  options,
  onChange,
}: StandalonePillBoxMultiSelectProps<T>) {
  return (
    <Listbox value={value} onChange={onChange} multiple>
      {({ open }) => (
        <>
          <div className="relative">
            <Listbox.Button className="relative w-full min-w-[200px] cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6">
              <span className="block truncate">{placeholder}</span>
              {value.length > 0 && (
                <div
                  className={
                    'absolute right-8 top-0 bottom-0 flex items-center'
                  }
                >
                  <Pill
                    color={'bg-gray-100'}
                    textColor={'text-gray-700'}
                    ringColor={'ring-gray-300'}
                    className={'min-w-[36px]'}
                  >
                    {value.length}
                  </Pill>
                </div>
              )}
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {options.map(option => (
                  <Listbox.Option
                    key={option.id.toString()}
                    className={({ active }) =>
                      classNames(
                        active ? 'bg-blue-600 text-white' : 'text-gray-900',
                        'relative cursor-default select-none py-2 pl-8 pr-4'
                      )
                    }
                    value={option.value}
                  >
                    {({ selected, active }) => (
                      <>
                        <StatusPill color={option.color}>
                          {option.label}
                        </StatusPill>

                        <span
                          className={classNames(
                            active ? 'text-white' : 'text-blue-600',
                            'absolute inset-y-0 left-0 flex items-center pl-1.5'
                          )}
                        >
                          <input
                            id={option.id.toString()}
                            name={option.id.toString()}
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                            checked={selected}
                            onChange={() => void 0}
                          />
                        </span>
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
  )
}
